import { useCallback } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useSettings } from "../useSetting";
import { ISetting } from "@/domain/setting.interface";
import { displayDateOfBirth } from "../../utils/temporal.utils";

dayjs.extend(utc);
dayjs.extend(timezone);

type DateFormat = "MM/DD/YYYY" | "DD/MM/YYYY" | "YYYY-MM-DD";

/**
 * Hook for date formatting based on the countryFormat account setting.
 * Uses Temporal.PlainDate under the hood to avoid timezone offset issues.
 * Time-bearing values are rendered in the account's IANATimeZone setting.
 */
export const useDateFormat = () => {
	const { data: settings } = useSettings();
	const { countryFormat, IANATimeZone } = settings || ({} as ISetting);
	const dateFormat =
		((countryFormat as { dateFormat?: string })?.dateFormat as DateFormat) ||
		"MM/DD/YYYY";
	const tz = (IANATimeZone as string | undefined) || undefined;

	/**
	 * Format a date of birth for display.
	 * Handles both legacy ISO dates ("1993-07-02T06:00:00.000Z")
	 * and already-formatted dates ("07/02/1993").
	 */
	const formatDob = useCallback(
		(dateString: string | null | undefined): string =>
			displayDateOfBirth(dateString, dateFormat) || "-",
		[dateFormat],
	);

	/**
	 * Date-only formatter — does NOT apply IANATimeZone.
	 * Use for values that represent a plain calendar date (no time-of-day semantics),
	 * e.g. enrollment date, report date. For instants in time, use formatDateTime.
	 */
	const formatDate = useCallback(
		(value: string | Date | null | undefined): string =>
			value ? dayjs(value).format(dateFormat) : "-",
		[dateFormat],
	);

	const formatDateTime = useCallback(
		(value: string | Date | null | undefined): string => {
			if (!value) return "—";
			const d = dayjs(value);
			return (tz ? d.tz(tz) : d).format(`${dateFormat} hh:mm A`);
		},
		[dateFormat, tz],
	);

	return { dateFormat, formatDob, formatDate, formatDateTime };
};
